///
/// EarthBrowser
///
/// Created by Matt Giger on 10/23/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Account control in the top-right: the same 36px glass pill as the rest
/// of the chrome, holding the avatar (signed in) or a user glyph (signed
/// out). Opens the app menu - identity, account settings, preferences,
/// help, version, developer tools and sign out - so the small "who am I /
/// what's mine" surface sits between the avatar and the full settings
/// dialog like every other account-bearing app.
///
/// Signed out, the header row is the sign-in call to action. It replaces
/// the former always-visible "Free account" chip: the conversion ladder's
/// rung 0 is now this row plus the notification-slot banner at rung 1, so
/// the chrome carries verbs and icons only.
///
/// Long-pressing the pill for 2s unlocks the developer tools (SQL
/// terminal, storage manager), which then appear in the menu.
///

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Popover, PopoverContent } from "@/components/ui/popover"
import { Switch } from "@/components/ui/switch"
import { useCommandRegistry } from "@/hooks/useCommandRegistry"
import { findPaidSubscription } from "@/hooks/useEntitlements"
import { useLongPress } from "@/hooks/useLongPress"
import { setAuthOpen, setOnboardingOpen, setProfileOpen } from "@/redux/dialogSlice"
import { setTourSettings } from "@/redux/earthBrowserSlice"
import { conversionRung } from "@/redux/engagementSlice"
import { useAppDispatch, useAppSelector } from "@/redux/store"
import { useGetUserProfileQuery, useGetUserSubscriptionQuery, useSignOutUserMutation } from "@/redux/userApi"
import { track } from "@/utils/analytics"
import { datasetUrl } from "@/utils/datasetUrl"
import { cn } from "@/utils/utils"
import * as PopoverPrimitive from "@radix-ui/react-popover"
import { GraduationCap, LogIn, LogOut, Settings, Timer, User } from "lucide-react"
import { useCallback, useRef, useState } from "react"
import type { ReactNode } from "react"

interface AccountMenuProps {
	/** Called when the pill is long-pressed (unlocks dev tools) */
	onDevtoolsUnlock?: () => void
}

export function AccountMenu({ onDevtoolsUnlock }: AccountMenuProps) {
	const dispatch = useAppDispatch()
	const [open, setOpen] = useState(false)
	const triggerRef = useRef<HTMLButtonElement>(null)

	// The user slice is the signed-in source of truth shared with the
	// profile dialog and ProtectedComponent; the query only tells us
	// whether the launch fetch is still in flight (dims the glyph)
	const { isLoading: isProfileLoading } = useGetUserProfileQuery()
	const userProfile = useAppSelector(state => state.user.userProfile)
	const signedIn = userProfile !== undefined
	const { data: subscriptions } = useGetUserSubscriptionQuery(undefined, { skip: !signedIn })
	const [signOutUser] = useSignOutUserMutation()

	const autoOrbit = useAppSelector(state => state.earthbrowser.tourSettings.autoOrbit)
	const rung = useAppSelector(state => conversionRung(state.engagement))
	const { commands: registryCommands } = useCommandRegistry()
	// Dev tools only register in dev mode or after the long-press unlock
	const devCommands = registryCommands.filter(cmd => cmd.category === "Developer" && cmd.enabled !== false)

	const close = useCallback(() => setOpen(false), [])

	// The pill is a long-press target, so it drives the popover itself
	// (PopoverAnchor) instead of using PopoverTrigger's click handling
	const longPress = useLongPress({
		onLongPress: () => {
			onDevtoolsUnlock?.()
			setOpen(true)
		},
		onClick: () => setOpen(previous => !previous),
	})

	const handleSignIn = () => {
		// Same event the former corner chip emitted: it's still the rung-0
		// CTA, so the admin funnel stays continuous
		track("register_chip_clicked", { rung })
		close()
		dispatch(setAuthOpen(true))
	}

	const handleSettings = () => {
		close()
		dispatch(setProfileOpen(true))
	}

	const handleTutorial = () => {
		close()
		dispatch(setOnboardingOpen(true))
	}

	const handleSignOut = async () => {
		close()
		await signOutUser()
	}

	const fullName = userProfile ? `${userProfile.first_name} ${userProfile.last_name}` : ""
	const initials = userProfile
		? (userProfile.first_name?.[0] ?? "").toUpperCase() + (userProfile.last_name?.[0] ?? "").toUpperCase()
		: ""
	const paid = findPaidSubscription(subscriptions)
	const planLabel = subscriptions ? (paid ? paid.name || "Pro" : "Free plan") : undefined

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverPrimitive.Anchor asChild>
				<button
					ref={triggerRef}
					type="button"
					{...longPress}
					className={cn(
						"flex items-center justify-center h-9 w-9 rounded-xl",
						"bg-black/40 backdrop-blur-xl border border-white/20 shadow-2xl",
						"text-white/80 hover:text-white hover:bg-black/50 hover:border-white/30",
						"transition-all duration-300 touch-manipulation select-none",
						"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500",
						open && "bg-black/50 border-white/30 text-white"
					)}
					aria-label={signedIn ? `Account menu for ${fullName}` : "Account menu"}
					aria-expanded={open}
					aria-haspopup="menu"
					data-account-menu
				>
					{signedIn ? (
						<Avatar className="h-7 w-7 rounded-full">
							<AvatarImage src={datasetUrl(userProfile.image_url)} alt="" className="object-cover" />
							<AvatarFallback className="rounded-full bg-gradient-to-br from-blue-500 via-cyan-500 to-teal-500 text-[11px] font-semibold text-white">
								{initials}
							</AvatarFallback>
						</Avatar>
					) : (
						<User className={cn("w-4 h-4", isProfileLoading && "opacity-50")} />
					)}
				</button>
			</PopoverPrimitive.Anchor>
			<PopoverContent
				align="end"
				sideOffset={8}
				collisionPadding={8}
				// A click on the pill while open must not both dismiss (outside
				// interaction) and re-toggle (the pill's own click)
				onInteractOutside={event => {
					if (triggerRef.current?.contains(event.target as Node)) event.preventDefault()
				}}
				className={cn(
					"z-[1100] w-64 p-1.5 text-white",
					"bg-black/40 backdrop-blur-xl border border-white/20 rounded-xl shadow-2xl"
				)}
				role="menu"
				aria-label="Account"
			>
				{signedIn ? (
					<div className="flex items-center gap-3 px-2 py-2">
						<Avatar className="h-10 w-10 rounded-full flex-shrink-0">
							<AvatarImage src={datasetUrl(userProfile.image_url)} alt="" className="object-cover" />
							<AvatarFallback className="rounded-full bg-gradient-to-br from-blue-500 via-cyan-500 to-teal-500 text-sm font-semibold text-white">
								{initials}
							</AvatarFallback>
						</Avatar>
						<div className="flex-1 min-w-0">
							<div className="text-sm font-semibold text-white truncate">{fullName}</div>
							<div className="text-xs text-white/55 truncate">
								{planLabel ? `${planLabel} · ` : ""}@{userProfile.username}
							</div>
						</div>
					</div>
				) : (
					<button
						onClick={handleSignIn}
						role="menuitem"
						className={cn(
							"w-full flex items-center gap-3 px-2 py-2 rounded-lg text-left",
							"bg-cyan-500/10 border border-cyan-500/30 hover:bg-cyan-500/20",
							"transition-colors duration-200",
							"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
						)}
					>
						<div className="flex items-center justify-center w-9 h-9 rounded-full bg-cyan-500/20 flex-shrink-0">
							<LogIn className="w-4 h-4 text-cyan-300" />
						</div>
						<div className="flex-1 min-w-0">
							<div className="text-sm font-semibold text-white">Sign in or create account</div>
							<div className="text-[11px] text-white/55 leading-snug">
								Free · keeps your views and places on every device
							</div>
						</div>
					</button>
				)}

				<MenuDivider />

				{signedIn && (
					<MenuItem icon={<Settings className="w-4 h-4" />} label="Account settings" onClick={handleSettings} />
				)}
				<MenuItem
					icon={<Timer className="w-4 h-4" />}
					label="Auto-orbit when idle"
					hint="Slowly circle the view when idle"
					onClick={() => dispatch(setTourSettings({ autoOrbit: !autoOrbit }))}
					trailing={
						<Switch
							checked={autoOrbit}
							onCheckedChange={checked => dispatch(setTourSettings({ autoOrbit: checked }))}
							onClick={event => event.stopPropagation()}
							aria-label="Auto-orbit when idle"
							className="h-5 w-9 data-[state=checked]:bg-cyan-500 data-[state=unchecked]:bg-white/20 [&>span]:h-4 [&>span]:w-4 [&>span]:bg-white [&>span[data-state=checked]]:translate-x-4"
						/>
					}
				/>
				<MenuItem icon={<GraduationCap className="w-4 h-4" />} label="Show tutorial" onClick={handleTutorial} />

				{devCommands.length > 0 && (
					<>
						<MenuDivider label="Developer" />
						{devCommands.map(cmd => (
							<MenuItem
								key={cmd.id}
								icon={cmd.icon}
								label={cmd.title}
								onClick={() => {
									close()
									cmd.onSelect()
								}}
							/>
						))}
					</>
				)}

				{signedIn && (
					<>
						<MenuDivider />
						<MenuItem icon={<LogOut className="w-4 h-4" />} label="Sign out" onClick={() => { void handleSignOut() }} />
					</>
				)}

				<div className="flex items-center justify-between px-2 pt-2 pb-1">
					<span className="text-[10px] text-white/35">EarthBrowser</span>
					<span className="text-[10px] font-mono text-white/35">v{__APP_VERSION__}</span>
				</div>
			</PopoverContent>
		</Popover>
	)
}

function MenuDivider({ label }: { label?: string }) {
	if (!label) {
		return <div className="my-1 h-px bg-white/10" role="separator" />
	}
	return (
		<div className="flex items-center gap-2 px-2 pt-2 pb-0.5" role="separator">
			<span className="text-[10px] font-semibold uppercase tracking-wider text-white/35">{label}</span>
			<div className="flex-1 h-px bg-white/10" />
		</div>
	)
}

interface MenuItemProps {
	icon: ReactNode
	label: string
	hint?: string
	trailing?: ReactNode
	onClick: () => void
}

// A div rather than a button so a row can host its own control (the
// auto-orbit switch) without nesting interactive elements
function MenuItem({ icon, label, hint, trailing, onClick }: MenuItemProps) {
	return (
		<div
			onClick={onClick}
			onKeyDown={event => {
				if (event.key === "Enter" || event.key === " ") {
					event.preventDefault()
					onClick()
				}
			}}
			role="menuitem"
			tabIndex={0}
			className={cn(
				"cursor-pointer",
				"w-full flex items-center gap-2.5 px-2 py-1.5 rounded-lg text-left",
				"text-white/85 hover:text-white hover:bg-white/10 active:bg-white/15",
				"transition-colors duration-200",
				"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
			)}
		>
			<span className="flex items-center justify-center w-5 text-white/60 flex-shrink-0 [&>svg]:w-4 [&>svg]:h-4">{icon}</span>
			<span className="flex-1 min-w-0">
				<span className="block text-sm truncate">{label}</span>
				{hint && <span className="block text-[10px] text-white/45 truncate">{hint}</span>}
			</span>
			{trailing}
		</div>
	)
}
